import React from 'react';
import Helmet from 'react-helmet';
import Waypoint from 'react-waypoint';
import debounce from 'lodash/debounce';

import Galleries from 'api/resources/galleries';
import getErrors from 'utils/getErrors';
import { Deserializer } from 'jsonapi-serializer';

import CardsStub from 'admin-editor/components/CardsStub';
import GalleriesIcon from 'icons/Galleries';
import FiltersDropdown from 'components/FiltersDropdown';
import SearchForm from 'components/SearchForm';
import GalleryItem from 'admin-editor/components/GalleryItem';

import ErrorText from 'styled/Page/ErrorText';
import PrimaryLink from 'styled/PrimaryLink';
import Main from 'styled/Page/Main';
import FilterWrapper from 'styled/Page/FilterWrapper';
import CenteredContainer from 'styled/Page/CenteredContainer';
import RightColumn from 'styled/Page/RightColumn';
import CardsList from 'styled/Page/CardsList';

const SORT_FILTERS = [
  {
    label: 'От новых к старым',
    value: '-created_at',
  }, {
    label: 'От старых к новым',
    value: 'created_at',
  },
];

const PAGE_SIZE = 12;
const STUBS_COUNT = (PAGE_SIZE / 3) * 2; // две строки заглушек = 8 штук

class GalleriesPage extends React.PureComponent {
  state = {
    pageNumber: 0,
    hasMore: false,
    loading: false,
    galleries: [],
    errors: [],
    searchValue: undefined,
    sort: SORT_FILTERS[0],
  }

  loadGalleriesWithDebounce = debounce(() => {
    window.scrollTo(0, 0);
    this.setState({ galleries: [], pageNumber: 0 }, this.handleGalleriesReload);
  }, 500);

  componentDidMount() {
    this.loadGalleries();
  }

  handleGalleriesReload = () => {
    if (this.state.loading) return;

    window.scrollTo(0, 0);

    this.setState({
      galleries: [],
      pageNumber: 0,
    }, this.loadGalleries);
  }

  loadGalleries = async () => {
    this.setState({
      loading: true,
      errors: [],
    });

    try {
      const response = await Galleries.getAll({
        page: {
          number: this.state.pageNumber,
          size: PAGE_SIZE,
        },
        filter: {
          q: this.state.searchValue || undefined,
        },
        sort: this.state.sort.value,
        fields: {
          gallery: 'title,images_count,created_at,main_gallery_file',
          image: 'url',
        },
        include: 'main_gallery_file.image',
      });

      const galleries = await new Deserializer({ keyForAttribute: 'camelCase' }).deserialize(response);

      this.setState(prevState => ({
        galleries: prevState.galleries.concat(galleries),
        loading: false,
        hasMore: galleries.length === PAGE_SIZE,
        pageNumber: prevState.pageNumber + 1,
      }));
    } catch (err) {
      getErrors(err).then((errors) => {
        this.setState({
          errors,
          loading: false,
        });
      });
    }
  }

  handleSearchChange = (e) => {
    this.setState({ searchValue: e.target.value });
    this.loadGalleriesWithDebounce();
  }

  handleSearchSubmit = (e) => {
    e.preventDefault();

    this.handleGalleriesReload();
  }

  handleSearchClose = () => {
    this.setState({
      searchValue: undefined,
      pageNumber: 0,
    }, this.handleGalleriesReload);
  }

  handleFilterChange = (sort = {}) => {
    this.setState({ sort }, this.handleGalleriesReload);
  }

  render() {
    return (
      <Main>
        <Helmet><title>Галереи</title></Helmet>

        <div style={{ width: '100%' }}>
          <FilterWrapper>
            <FiltersDropdown
              filters={SORT_FILTERS}
              style={{ width: '170px', marginRight: 'auto' }}
              onFilterChange={this.handleFilterChange}
              activeFilter={this.state.sort}
            />

            <SearchForm
              onChange={this.handleSearchChange}
              onSubmit={this.handleSearchSubmit}
              onClose={this.handleSearchClose}
              value={this.state.searchValue}
            />

            <PrimaryLink
              to="/galleries/new"
              style={{ marginLeft: '20px' }}
            >
              добавить галерею
            </PrimaryLink>
          </FilterWrapper>

          <CardsList>
            {this.state.galleries.length !== 0 &&
              this.state.errors.length === 0 && (
                this.state.galleries.map(item => (
                  <GalleryItem
                    id={item.id}
                    key={item.id}
                    title={item.title}
                    imagesCount={item.imagesCount}
                    createdAt={item.createdAt}
                    mainImageUrl={item.galleryFile && item.galleryFile.image && item.galleryFile.image.url}
                  />
                ))
              )
            }

            {this.state.loading && (
              Array.from({ length: STUBS_COUNT }, () => <CardsStub key={Math.random()} showCountStub />)
            )}
          </CardsList>

          {this.state.errors.length > 0 && (
            <CenteredContainer>
              {this.state.errors.map(error => (
                <ErrorText role="alert" key={error}>{error}</ErrorText>
              ))}
            </CenteredContainer>
          )}

          {!this.state.loading &&
            this.state.galleries.length === 0 &&
            this.state.errors.length === 0 && (
            <CenteredContainer>
              <GalleriesIcon />

              <ErrorText>Галереи не найдены</ErrorText>
            </CenteredContainer>
          )}

          {this.state.hasMore && <Waypoint onEnter={this.loadGalleries} />}
        </div>

        <RightColumn />
      </Main>
    );
  }
}

export default GalleriesPage;
